import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
	Autocomplete, Box, Button, Chip, FormControlLabel, IconButton, MenuItem, Pagination, Select,
	Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
	TableRow, TextField, Typography,
} from '@mui/material'
import { dsColorNew } from '@palette'
import { alpha } from '@mui/material/styles'
import {
	AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppBulkEditAction, AppNote,
	AppAntSwitch, AppToolbarCreateButton, secondaryButtonSx,
} from '@components'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { DeleteOutline as DeleteIcon, ContentCopyOutlined as CopyIcon, Add as AddIcon, RuleOutlined as RuleIcon, UploadFileOutlined as UploadIcon, DownloadOutlined as DownloadIcon, SearchOutlined as SearchIcon, FilterAltOutlined as FilterIcon, OpenInNewOutlined as OpenInNewIcon, WarningAmberOutlined as WarningIcon, ShieldOutlined as ShieldIcon } from 'Pages/DsRepricing/@components/icons'

import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { useHighlight, highlightSx } from 'Pages/DsRepricing/Setup/@services/useHighlight'
import { useOfficeScope } from 'Pages/DsRepricing/Setup/@services/useOfficeScope'
import { uid } from 'Pages/DsRepricing/Setup/@mocks'
import {
	useSetupCollection, useAxisCatalog, PREPAID_OPTIONS,
	prepaidKey, prepaidValue, idKey, idValue,
	resolveFranchise, resolveInsurance, resolvePrepaid,
} from 'Pages/DsRepricing/Setup/@services'
import { setupApi } from 'Pages/DsRepricing/Setup/@services/api'
import {
	Surface, SurfaceHeader, EmptyState, LoadingState, HelpNote,
} from 'Pages/DsRepricing/Setup/@components/Surface'
import { inputSx, rowHoverSx, cellPadSx, headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import DateRangeField from 'Pages/DsRepricing/@components/DateRangeField'
import DurationRangeField from 'Pages/DsRepricing/@components/DurationRangeField'
import UnsavedChip from 'Pages/DsRepricing/Setup/@components/UnsavedChip'
import { futurePresets, sameAsPreset } from 'Pages/DsRepricing/@services/dateRange'
import { displayDay } from '@utils'
import { IconSeeker } from '@ui-seeker/core'

// Persistence: @services/useSetupCollection (entity 'priceLimits').
const PAGE_SIZE = 25

/**
 * Price-limit rule shape (v4):
 *   scope_ps_provider_id / scope_is_prepaid / scope_ps_insurance_id — the
 *     same three axes a rate-card face uses, in Price Seeker's ids
 *   scope_office_group / scope_office      (mutually exclusive)
 *   scope_categories: [{type:'macro'|'sipp', code}, ...]
 *   scope_duration: null | {unit, min, max}
 *   date_from / date_to (day granularity)
 *   min_price / max_price
 *
 * v4 aligns Price Limits with Rate Cards taxonomy so a single filter
 * (franchise + tariff + insurance) works across the whole Setup surface.
 */
const DEFAULT_LIMITS = [
	{ id: uid(),
	  scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	  scope_office_group: null, scope_office: null,
	  scope_categories: null, scope_duration: null,
	  date_from: null, date_to: null,
	  min_price: 18, max_price: null },
	{ id: uid(),
	  scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	  scope_office_group: 'airports', scope_office: null,
	  scope_categories: [{ type: 'macro', code: 'mini' }],
	  scope_duration: { unit: 'days', min: 1, max: 3 },
	  date_from: null, date_to: null,
	  min_price: 22, max_price: null },
	{ id: uid(),
	  scope_ps_provider_id: null, scope_is_prepaid: true, scope_ps_insurance_id: null,
	  scope_office_group: null, scope_office: 'LPA-AP',
	  scope_categories: [{ type: 'sipp', code: 'CDMR' }],
	  scope_duration: { unit: 'weeks', min: 1, max: 2 },
	  date_from: '2026-12-20', date_to: '2027-01-05',
	  min_price: 45, max_price: null },
	{ id: uid(),
	  scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	  scope_office_group: null, scope_office: 'TFS-AP',
	  scope_categories: [{ type: 'sipp', code: 'FVAR' }],
	  scope_duration: null,
	  date_from: null, date_to: null,
	  min_price: 55, max_price: 200 },
	// A date protection: any rental of up to 30 days that crosses
	// 26–31 Dec is floored at 50, whatever its pickup date.
	{ id: uid(),
	  scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	  scope_office_group: null, scope_office: null,
	  scope_categories: null, scope_duration: null,
	  date_from: '2026-12-26', date_to: '2026-12-31',
	  protects_dates: true, name: 'Christmas',
	  min_price: 50, max_price: null },
]

function migrateLimit(r) {
	if (!r || typeof r !== 'object') return r
	let scope_categories = r.scope_categories
	if (scope_categories === undefined) {
		const list = []
		if (r.scope_macro) list.push({ type: 'macro', code: r.scope_macro })
		if (r.scope_sipp)  list.push({ type: 'sipp',  code: r.scope_sipp })
		scope_categories = list.length ? list : null
	}
	let scope_duration = r.scope_duration
	if (scope_duration === undefined) {
		const b = r.scope_duration_bucket
		if (!b) scope_duration = null
		else {
			const m = /^(\d+)(?:-(\d+)|(\+))?$/.exec(b)
			scope_duration = m
				? { unit: 'days', min: Number(m[1]), max: m[2] ? Number(m[2]) : null }
				: null
		}
	}
	// v4 → v5: the three axes became Price Seeker ids (2026-09-24).
	// A stored slug cannot be resolved to an id here — the catalogue is
	// async and this runs on raw rows — so it is dropped to "any"
	// rather than sent to a server that would reject it. The tables
	// were empty when the column migration ran, so nothing real is lost.
	const scope_ps_provider_id  = r.scope_ps_provider_id  ?? null
	const scope_is_prepaid      = r.scope_is_prepaid      ?? null
	const scope_ps_insurance_id = r.scope_ps_insurance_id ?? null

	const {
		scope_macro, scope_sipp, scope_duration_bucket,
		scope_brand, scope_product,
		scope_franchise, scope_tariff_type, scope_insurance_type,
		...rest
	} = r
	return {
		...rest,
		scope_ps_provider_id, scope_is_prepaid, scope_ps_insurance_id,
		scope_categories,
		scope_duration,
	}
}

const EMPTY_FILTERS = {
	search: '',
	kind:            '*',   // '*' | 'protection' | 'pickup' 
	franchise_scope: '*',
	tariff_scope:    '*',
	insurance_scope: '*',
	office_scope:    '*',
	category_scope:  '*',
	duration_unit:   '*',
	has_price:       '*',
	date_from:       '',   // YYYY-MM-DD, inclusive
	date_to:         '',   // YYYY-MM-DD, inclusive
}

export default function PriceLimitsTab() {
	// The three scope axes are Price Seeker ids and their names live in
	// the tenant's own catalogue, not in a constant.
	const axes = useAxisCatalog()
	// Persisted through webback (or the localStorage mock behind the flag);
	// the collection is diffed and synced per rule — see @services.
	const [limits, setLimits, sync] = useSetupCollection('priceLimits', {
		mockDefault: DEFAULT_LIMITS, normalise: migrateLimit,
	})
	const [filters, setFilters] = useState(EMPTY_FILTERS)
	const [page, setPage]       = useState(1)
	const [csvOpen, setCsvOpen] = useState(false)

	// The matrices this tenant has, for the binding rows to point at.
	// Read-only here: they are authored in their own tab.
	const [matrices, setMatrices] = useState([])
	const [matricesLoading, setMatricesLoading] = useState(true)
	useEffect(() => {
		let alive = true
		setupApi.priceMatrices.list()
			.then(items => { if (alive) setMatrices(items || []) })
			.catch(() => { if (alive) setMatrices([]) })
			.finally(() => { if (alive) setMatricesLoading(false) })
		return () => { alive = false }
	}, [])

	function addRule() {
		setLimits(prev => [{
			id: uid(),
			scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
			scope_office_group: null, scope_offices: null,
			scope_categories: null,
			scope_duration: null,
			date_from: null, date_to: null,
			protects_dates: false, name: null,
			matrix_id: null,
			min_price: 20, max_price: null,
		}, ...prev])
		setPage(1)
	}
	/**
	 * A row whose floors come from a matrix.
	 *
	 * Category and duration stay null and are not editable on it: the
	 * matrix owns those two axes. That is not tidiness — it is what
	 * keeps a hand-written rule that pins a SIPP more specific than a
	 * binding on the same scope, so the exception wins without anyone
	 * programming a precedence (CU-869f9n466).
	 */
	function addMatrixBinding() {
		setLimits(prev => [{
			id: uid(),
			scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
			scope_office_group: null, scope_offices: null,
			scope_categories: null,
			scope_duration: null,
			date_from: null, date_to: null,
			protects_dates: false, name: null,
			matrix_id: '',
			min_price: null, max_price: null,
		}, ...prev])
		setPage(1)
	}
	function updateRule(id, patch) {
		setLimits(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r))
	}
	// Turning the protection on moves the row to its own group. The
	// operator just did it, so the jump is explained — unlike the
	// reorder-on-edit Curro rejected on 2026-09-28 — and the row is
	// scrolled to and flashed so the eye follows it (UX 3).
	const [flashId, setFlashId] = useState(null)
	useEffect(() => {
		if (!flashId) return
		const t = setTimeout(() => setFlashId(null), 2200)
		return () => clearTimeout(t)
	}, [flashId])
	function toggleProtection(id, on) {
		updateRule(id, on ? { protects_dates: true, max_price: null } : { protects_dates: false, name: null })
		setFlashId(id)
	}
	function deleteRule(id) {
		setLimits(prev => prev.filter(r => r.id !== id))
	}
	function cloneRule(id) {
		// Directly below its original, not at the top: a clone is a
		// variation on the row you are looking at, and putting it on
		// another page to be found later defeats the point. It carries
		// everything except the identity.
		setLimits(prev => {
			const i = prev.findIndex(r => r.id === id)
			if (i < 0) return prev
			const copy = { ...prev[i], id: uid() }
			return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)]
		})
	}
	function importFromCsv(newLimits, mode) {
		setLimits(prev => mode === 'replace' ? newLimits : [...newLimits, ...prev])
		setPage(1)
		setCsvOpen(false)
	}

	// Insertion order, newest first — not specificity. The engine
	// resolves limits by scoring them in Python (`most_specific`), so
	// this order was only ever presentation, and as presentation it
	// worked against the operator: editing an amount could move the row
	// several places for a reason invisible on screen (Curro,
	// 2026-09-28). `_specificity` is still computed, for the column
	// that shows it.
	const ordered = useMemo(() => (
		limits.map(r => ({ ...r, _specificity: specificity(r) }))
	), [limits])

	const filtered = useMemo(() => applyFilters(ordered, filters, axes), [ordered, filters, axes])
	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
	// A cap in the Activity log links here with `?highlight=<id>`. The
	// limit it names can be on any page, so the page follows the link
	// rather than making the operator hunt (Curro, 2026-09-28).
	const highlight = useHighlight()

	// One table, two kinds of row. A binding is a limit for the resolver
	// but was dressed on screen as one — "set by the matrix" twice and
	// the picker under Min €/day (Curro, 2026-10-03). Bindings first,
	// individual limits after, each group under its own header row.
	// Three groups since CU-869fe8jrk: date protections first — they
	// sit outside the specificity ranking (the highest floor wins), so
	// a separator drawn between one and an ordinary limit would lie.
	const kindOf = (r) => r.protects_dates ? 'protection' : r.matrix_id != null ? 'matrix' : 'limit'
	const grouped = useMemo(() => [
		...filtered.filter(r => kindOf(r) === 'protection'),
		...filtered.filter(r => kindOf(r) === 'matrix'),
		...filtered.filter(r => kindOf(r) === 'limit'),
	], [filtered])
	const groupCount = { protection: 0, matrix: 0, limit: 0 }
	for (const r of grouped) groupCount[kindOf(r)] += 1
	// Below `grouped`, not above it: the dependency array is built
	// during the render, so naming `grouped` before its `const` threw
	// `ReferenceError: Cannot access 'grouped' before initialization`
	// on every render of this tab. Shipped 2026-10-03 with the merged
	// binding row and caught the same day.
	useEffect(() => {
		if (!highlight.id) return
		const at = grouped.findIndex(r => r.id === highlight.id)
		if (at >= 0) setPage(Math.floor(at / PAGE_SIZE) + 1)
	}, [highlight.id, grouped])
	const currentPage = Math.min(page, totalPages)
	const paged = grouped.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

	// If the operator picked a date range, walk it day by day and flag
	// any day NOT covered by at least one filtered rule. A rule with
	// null date_from/date_to counts as "always covering". We ignore
	// scope in this check — the intent is "is there ANY safety net for
	// this day?", not "is every combination covered".
	const uncoveredDays = useMemo(() => {
		if (!filters.date_from || !filters.date_to) return []
		const days = enumerateDays(filters.date_from, filters.date_to)
		if (days.length === 0) return []
		// Cap the scan so a comically wide range doesn't stall the UI.
		if (days.length > 366) return { tooBig: true, count: days.length }
		return days.filter(d => !filtered.some(r => ruleCoversDay(r, d)))
	}, [filters.date_from, filters.date_to, filtered])

	const activeFiltersCount =
		+(filters.search !== '') +
		+(filters.franchise_scope !== '*') +
		+(filters.tariff_scope !== '*') +
		+(filters.insurance_scope !== '*') +
		+(filters.office_scope !== '*') +
		+(filters.category_scope !== '*') +
		+(filters.duration_unit !== '*') +
		+(filters.has_price !== '*') +
		+(filters.kind !== '*')

	// Taxonomy values actually present in the loaded data — drive filter
	// dropdowns so the options match reality, not catalog fiction.
	// Keyed as strings so the filter dropdowns can hold them: `false` is
	// a real tariff scope and `uniqueNonNull` would drop it as falsy.
	const franchisesInUse = useMemo(
		() => uniqueNonNull(limits.map(l => idKey(l.scope_ps_provider_id))), [limits])
	const tariffsInUse    = useMemo(
		() => uniqueNonNull(limits.map(l => prepaidKey(l.scope_is_prepaid))), [limits])
	const insurancesInUse = useMemo(
		() => uniqueNonNull(limits.map(l => idKey(l.scope_ps_insurance_id))), [limits])

	return (
		<Surface padded={false}>
			<Box sx={{ p: 2.5 }}>
				<SurfaceHeader
					// The card's notes, with its description, behind the ⓘ of its title
					info={<>
						<Box>
						<strong>Resolution:</strong> the ONE rule with the most specific
						match wins. Order of specificity: <em>franchise + tariff + insurance
						+ specific office</em> beats <em>group</em> beats <em>any</em>;
						ACRISS codes beat macros; a narrow duration or date range beats wildcards.
						You never set priority by hand — it is derived from what fields are
						filled. <strong>Date protections</strong> do not take part in this
						order: the engine takes the highest of the ordinary floor and every
						protection the rental crosses. Two overlapping protections resolve the
						same way — the higher one wins. A new or changed limit takes effect the
						next time the engine prices the cell — prices already in the PMS are
						not rewritten.
						</Box>
					</>}
					icon="shield-halved"
					title="Price limits"
					subtitle={<><strong>Hard floors</strong> (and optional ceilings) that
					Strategies can never pass. <strong>The most specific rule wins</strong>.
					Rules can go all the way down to <em>franchise + tariff + insurance +
					specific ACRISS + specific office + a single day</em>. Same taxonomy as
					Rate Cards. Bulk-load via CSV — RateGain / Carwiz format is auto-detected.
					{' '}<strong>Date protections</strong> are the one exception: they never
					compete on specificity — a cell's floor is the highest of the ordinary
					rule and every protection the rental crosses.</>}
					actions={
						<Stack direction="row" gap={1}>
							<Button size="medium" variant="outlined" startIcon={<DownloadIcon/>}
							        onClick={() => downloadCsv(limits)}
							        sx={secondaryButtonSx}>
								Export CSV
							</Button>
							<Button size="medium" variant="outlined" startIcon={<UploadIcon/>}
							        onClick={() => setCsvOpen(true)}
							        sx={secondaryButtonSx}>
								Import CSV
							</Button>
							<Button size="medium" variant="outlined" startIcon={<AddIcon/>}
							        onClick={addMatrixBinding}
							        sx={secondaryButtonSx}>
								Add matrix
							</Button>
							<AppToolbarCreateButton divider={false} label="Add limit" icon="plus"
							                        onClick={addRule} sx={{ height: 38 }}/>
						</Stack>
					}
					sx={{ mb: 2 }}
				/>

				<FiltersBar filters={filters}
				            activeCount={activeFiltersCount}
				            onChange={(next) => { setFilters(next); setPage(1) }}
				            total={limits.length}
				            filteredTotal={filtered.length}
				            franchisesInUse={franchisesInUse}
				            tariffsInUse={tariffsInUse}
				            insurancesInUse={insurancesInUse}/>

				<CoverageGapWarning filters={filters} uncovered={uncoveredDays}/>

				{sync.loading ? (
					<LoadingState what="price limits"/>
				) : paged.length === 0 ? (
					<EmptyState
						icon={<RuleIcon/>}
						title={activeFiltersCount > 0 ? 'No limits match these filters'
						                              : 'No price limits yet'}
						description={activeFiltersCount > 0
						  ? 'Clear the filters above or add a new limit that matches your criteria.'
						  : 'Add a global default first (all wildcards), then layer surgical overrides on top for specific franchises, offices, categories, or seasons. Or import a RateGain export straight in.'}
						action={activeFiltersCount > 0
						  ? <Button variant="outlined"
						            onClick={() => setFilters(EMPTY_FILTERS)}
						            sx={{ ...secondaryButtonSx, height: 36 }}>
						      Clear filters
						    </Button>
						  : <Button variant="outlined" startIcon={<AddIcon/>}
						            onClick={addRule}
						            sx={{ ...secondaryButtonSx, height: 36 }}>
						      Add first limit
						    </Button>}
					/>
				) : (
					<>
						<TableContainer sx={{ border: '1px solid', borderColor: dsColorNew.grayBorder,
						                      borderRadius: '10px', overflow: 'auto' }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<HeadCell width={140}>Franchise</HeadCell>
										<HeadCell width={130}>Tariff type</HeadCell>
										<HeadCell width={150}>Insurance</HeadCell>
										<HeadCell width={200}>Office scope</HeadCell>
										<HeadCell width={280}>Category scope</HeadCell>
										<HeadCell width={220}>Duration</HeadCell>
										<HeadCell width={220}>Date range</HeadCell>
										<HeadCell align="right" width={100}>Min €/day</HeadCell>
										<HeadCell align="right" width={100}>Max €/day</HeadCell>
										<HeadCell width={50}></HeadCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{paged.map((r, idxInPage) => {
										const globalIdx = (currentPage - 1) * PAGE_SIZE + idxInPage
										const prev = globalIdx > 0 ? grouped[globalIdx - 1] : null
										const kind = kindOf(r)
										const sameKind = prev && kindOf(prev) === kind
										// No specificity separator among protections: they do
										// not compete on it (CU-869fe8jrk).
										const specificityDrop = sameKind && kind !== 'protection'
											&& r._specificity < prev._specificity
										const groupHeader = !sameKind ? (
											<GroupHeaderRow kind={kind} count={groupCount[kind]}/>
										) : null
										const lit = (highlight.active && r.id === highlight.id) || flashId === r.id
										// Held back rather than saved: its scope and dates
										// duplicate another row, which the server refuses.
										// A duplicate is what a clone IS until it is edited.
										const pending = sync.held?.has(r.id)
										// `''` is a binding still choosing its matrix;
										// `null` is an ordinary rule. Both are falsy, so
										// the test is on the key, not the value.
										const isBinding = r.matrix_id != null
										return (
											<React.Fragment key={r.id}>
											{groupHeader}
											<TableRow
											          ref={lit ? scrollIntoView : undefined}
											          sx={{
											              ...rowHoverSx,
											              ...highlightSx(lit),
											              verticalAlign: 'top',
											              borderTop: specificityDrop
											                ? '1px solid'
											                : 'none',
											              borderTopColor: dsColorNew.noteInfoBorder,
											          }}>
												<TableCell sx={cellPadSx}>
													<EnumScopeSelect value={idKey(r.scope_ps_provider_id)}
													                 onChange={(v) => updateRule(r.id, { scope_ps_provider_id: idValue(v) })}
													                 options={axes.franchises}
													                 allLabel="All franchises"
													                 variant="franchise"/>
												</TableCell>
												<TableCell sx={cellPadSx}>
													<EnumScopeSelect value={prepaidKey(r.scope_is_prepaid)}
													                 onChange={(v) => updateRule(r.id, { scope_is_prepaid: prepaidValue(v) })}
													                 options={PREPAID_OPTIONS}
													                 allLabel="All tariffs"
													                 variant="tariff"/>
												</TableCell>
												<TableCell sx={cellPadSx}>
													<EnumScopeSelect value={idKey(r.scope_ps_insurance_id)}
													                 onChange={(v) => updateRule(r.id, { scope_ps_insurance_id: idValue(v) })}
													                 options={axes.insurances}
													                 allLabel="All insurance"
													                 variant="insurance"/>
												</TableCell>
												<TableCell sx={cellPadSx}>
													<OfficeScopePicker rule={r}
													                   onChange={(p) => updateRule(r.id, p)}/>
												</TableCell>
												{isBinding ? (
													// A binding sets no categories, durations or prices of its
													// own: the matrix does, per ACRISS and rental days. One cell
													// says so, instead of five dressed as an ordinary limit.
													<TableCell colSpan={5} sx={{ ...cellPadSx, bgcolor: dsColorNew.tableRowAlt }}>
														<Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
															<Chip size="small" label="MATRIX"
															      sx={{ height: 22, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', borderRadius: '6px',
															            bgcolor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}/>
															<MatrixPicker value={r.matrix_id}
															              matrices={matrices}
															              loading={matricesLoading}
															              onChange={(v) => updateRule(r.id, { matrix_id: v })}/>
															<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, whiteSpace: 'nowrap' }}>
																floors per ACRISS × rental days, in force
															</Typography>
															<Stack gap={0.5}>
																<Stack direction="row" gap={0.75} alignItems="center">
																	{r.protects_dates && <ProtectionChip/>}
																	<DateRangeField from={r.date_from} to={r.date_to}
																	                placeholder={r.protects_dates ? 'Pick dates' : 'Always'}
																	                cleanable
																	                presets={[
																	                    sameAsPreset(paged[idxInPage - 1]?.date_from,
																	                                 paged[idxInPage - 1]?.date_to),
																	                    ...futurePresets(),
																	                ].filter(Boolean)}
																	                onChange={({ from, to }) =>
																	                    updateRule(r.id, { date_from: from, date_to: to })}/>
																</Stack>
																<DateModeToggle row={r}
																                onToggle={(on) => toggleProtection(r.id, on)}
																                onName={(name) => updateRule(r.id, { name })}/>
																<ProtectionDurationWarning row={r} all={limits} matrices={matrices}/>
															</Stack>
														</Stack>
													</TableCell>
												) : (<>
													<TableCell sx={cellPadSx}>
														<CategoryScopePicker value={r.scope_categories}
														                     onChange={(v) => updateRule(r.id, { scope_categories: v })}/>
													</TableCell>
													<TableCell sx={cellPadSx}>
														{/* Min and max, in days, open-ended — the shared control
														    (CU-869fhtp3a). No unit selector: there used to be one
														    — days, weeks, months — and the engine never read it,
														    so "2 to 4 weeks" matched 2 to 4 **day** rentals
														    (Curro, 2026-09-30). Everything is days. */}
														<DurationRangeField value={r.scope_duration} openEnd
														                    onChange={(v) => updateRule(r.id, { scope_duration: v && { unit: 'days', ...v } })}/>
														<ProtectionDurationWarning row={r} all={limits} matrices={matrices}/>
													</TableCell>
													<TableCell sx={cellPadSx}>
														{/* The row above is offered as a one-click range:
														    a season of limits is the same two dates on
														    every row, and that used to mean retyping
														    them (Curro, 2026-09-28). */}
														<Stack gap={0.5}>
															<Stack direction="row" gap={0.75} alignItems="center">
																{r.protects_dates && <ProtectionChip/>}
																<DateRangeField from={r.date_from} to={r.date_to}
																                placeholder={r.protects_dates ? 'Pick dates' : 'Always'}
																                cleanable
																                presets={[
																                    sameAsPreset(paged[idxInPage - 1]?.date_from,
																                                 paged[idxInPage - 1]?.date_to),
																                    ...futurePresets(),
																                ].filter(Boolean)}
																                onChange={({ from, to }) =>
																                    updateRule(r.id, { date_from: from, date_to: to })}/>
															</Stack>
															<DateModeToggle row={r}
															                onToggle={(on) => toggleProtection(r.id, on)}
															                onName={(name) => updateRule(r.id, { name })}/>
														</Stack>
													</TableCell>
													<TableCell align="right" sx={cellPadSx}>
														<TextField type="number" size="small"
														           value={r.min_price ?? ''}
														           onChange={(e) => updateRule(r.id, {
															           min_price: e.target.value === '' ? null : Number(e.target.value),
														           })}
														           inputProps={{ style: { width: 66, textAlign: 'right', fontWeight: 700 } }}
														           sx={inputSx}/>
													</TableCell>
													<TableCell align="right" sx={cellPadSx}>
														<TextField type="number" size="small"
														           value={r.max_price ?? ''}
														           placeholder="—"
														           disabled={!!r.protects_dates}
														           title={r.protects_dates ? 'A protection is a floor; it has no ceiling' : undefined}
														           onChange={(e) => updateRule(r.id, {
															           max_price: e.target.value === '' ? null : Number(e.target.value),
														           })}
														           inputProps={{ style: { width: 66, textAlign: 'right', fontWeight: 700 } }}
														           sx={inputSx}/>
													</TableCell>
												</>)}
												<TableCell sx={cellPadSx}>
													<Stack direction="row" gap={0.25} alignItems="center">
														{/* The server's refusal wins over the three client
														    reasons: a row it turned down is not "a duplicate
														    scope" — Thibault's was not — and must not fall
														    into that `else` (CU-869ffwj7a). */}
														<UnsavedChip refused={sync.refused?.get(r.id)}
														             stalled={sync.stalled?.has(r.id)}
														             reason={!pending ? null
														               : r.matrix_id === ''
														                 ? 'Pick a matrix and it saves itself.'
														                 : r.protects_dates && !(r.date_from && r.date_to)
														                   ? 'Pick the dates it protects and it saves itself.'
														                   : 'Its scope and dates match another limit. Change them and it saves itself.'}/>
														<IconButton size="small"
														            title="Duplicate this limit"
														            onClick={() => cloneRule(r.id)}
														            sx={{ color: dsColorNew.textMuted,
														                  '&:hover': { color: dsColorNew.appBlue,
														                               bgcolor: dsColorNew.pillActiveBg } }}>
															<CopyIcon fontSize="small"/>
														</IconButton>
														<IconButton size="small"
														            title="Delete this limit"
														            onClick={() => deleteRule(r.id)}
														            sx={{ color: dsColorNew.textMuted,
														                  '&:hover': { color: dsColorNew.dangerRed,
														                               bgcolor: dsColorNew.dangerBg } }}>
															<DeleteIcon fontSize="small"/>
														</IconButton>
													</Stack>
												</TableCell>
											</TableRow>
											</React.Fragment>
										)
									})}
								</TableBody>
							</Table>
						</TableContainer>

						{filtered.length > PAGE_SIZE && (
							<Stack direction="row" alignItems="center"
							       justifyContent="space-between"
							       sx={{ mt: 2, px: 0.5 }}>
								<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>
									Showing <strong>{(currentPage - 1) * PAGE_SIZE + 1}</strong>–
									<strong>{Math.min(currentPage * PAGE_SIZE, filtered.length)}</strong>
									{' '}of <strong>{filtered.length}</strong>
								</Typography>
								<Pagination count={totalPages} page={currentPage}
								            onChange={(_, v) => setPage(v)}
								            size="small" shape="rounded"/>
							</Stack>
						)}
					</>
				)}

			</Box>

			<CsvImportDialog open={csvOpen}
			                 onClose={() => setCsvOpen(false)}
			                 onImport={importFromCsv}/>
		</Surface>
	)
}

function uniqueNonNull(arr) {
	return [...new Set(arr.filter(Boolean))].sort()
}

// ---- Date-range coverage helpers ------------------------------------------
function enumerateDays(fromISO, toISO) {
	if (!fromISO || !toISO) return []
	const from = new Date(fromISO + 'T00:00:00Z')
	const to   = new Date(toISO   + 'T00:00:00Z')
	if (isNaN(from) || isNaN(to) || from > to) return []
	const out = []
	for (let d = new Date(from); d <= to; d.setUTCDate(d.getUTCDate() + 1)) {
		out.push(d.toISOString().slice(0, 10))
	}
	return out
}
// A day is considered "covered" ONLY by a rule with an explicit date
// window. Wildcard rules (date_from = date_to = null) are baseline
// safety nets — they don't tell the operator anything about whether a
// specific day was thought about. This lets the gap warning surface
// "you forgot to set anything for the holidays".
function ruleCoversDay(r, day) {
	const rFrom = r.date_from || null
	const rTo   = r.date_to   || null
	if (!rFrom && !rTo) return false           // wildcard is NOT specific coverage
	if (rFrom && day < rFrom) return false
	if (rTo   && day > rTo)   return false
	return true
}

// Human-readable compression of ISO days into contiguous runs.
// ["2026-09-08","2026-09-09","2026-09-11"] → ["Sep 8–9", "Sep 11"]
function compressDayRanges(days) {
	if (!days.length) return []
	const sorted = [...days].sort()
	const runs = []
	let start = sorted[0], prev = sorted[0]
	for (let i = 1; i < sorted.length; i++) {
		const d = sorted[i]
		const prevDate = new Date(prev + 'T00:00:00Z')
		prevDate.setUTCDate(prevDate.getUTCDate() + 1)
		const expected = prevDate.toISOString().slice(0, 10)
		if (d !== expected) { runs.push([start, prev]); start = d }
		prev = d
	}
	runs.push([start, prev])
	return runs.map(([a, b]) => a === b ? fmtDay(a) : `${fmtDay(a)} – ${fmtDay(b)}`)
}
// The app's day, read from the ISO text so the UTC day stays the day
const fmtDay = (iso) => displayDay(iso)

function CoverageGapWarning({ filters, uncovered }) {
	if (!filters.date_from || !filters.date_to) return null
	if (uncovered?.tooBig) {
		return (
			<AppNote severity="warning" sx={{ mb: 2 }}>
				<Box>
					<Typography sx={warningTitleSx}>
						Date range too wide to check coverage
					</Typography>
					<Typography sx={warningBodySx}>
						You selected {uncovered.count} days. Narrow the range to a year
						or less to see uncovered days here.
					</Typography>
				</Box>
			</AppNote>
		)
	}
	if (!Array.isArray(uncovered) || uncovered.length === 0) return null
	const runs = compressDayRanges(uncovered)
	return (
		<AppNote severity="warning" sx={{ mb: 2 }}>
			<Box>
				<Typography sx={warningTitleSx}>
					{uncovered.length === 1
					  ? '1 day in the selected range has no date-specific price limit'
					  : `${uncovered.length} days in the selected range have no date-specific price limit`}
				</Typography>
				<Typography sx={warningBodySx}>
					These days will only fall back to any wildcard rules you have
					(rules without dates). If you meant to set specific floors /
					ceilings for these dates — high-season, campaigns, etc. —
					you're missing them.
				</Typography>
				{/* This is the moment an operator discovers they need a second
				    season: the warning that says these days have no floor is
				    also where "duplicate the grid you already have" belongs
				    (UX, 2026-10-03). */}
				<Typography sx={{ ...warningBodySx, mt: 0.75 }}>
					A whole season's floors are usually a copy of another with a
					little on top:{' '}
					<Box component="a" href="?tab=price-matrix"
					     sx={{ color: dsColorNew.noteAmber, fontWeight: 700 }}>
						duplicate a matrix with adjustments
					</Box>
					{' '}and attach the copy to these dates.
				</Typography>
				<Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
					{runs.slice(0, 20).map((run, i) => (
						<Chip key={i} size="small" label={run}
						      sx={{ height: 22, fontSize: 12, fontWeight: 500, borderRadius: '999px',
						            bgcolor: dsColorNew.white, color: dsColorNew.noteAmber,
						            border: `1px solid ${dsColorNew.noteAmberBorder}` }}/>
					))}
					{runs.length > 20 && (
						<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber,
						                  alignSelf: 'center' }}>
							…and {runs.length - 20} more ranges
						</Typography>
					)}
				</Box>
			</Box>
		</AppNote>
	)
}

const warningTitleSx = {
	fontWeight: 600, color: dsColorNew.noteAmber,
	fontSize: 13, mb: 0.5,
}
const warningBodySx = {
	color: dsColorNew.noteAmber, fontSize: 13, lineHeight: 1.45,
}

// ---------------------------------------------------------------------------
// Filters bar
// ---------------------------------------------------------------------------
function FiltersBar({ filters, onChange, activeCount, total, filteredTotal,
                      franchisesInUse, tariffsInUse, insurancesInUse }) {
	// Same catalogues the row pickers read, memoised at module level so
	// asking here costs nothing.
	const { offices, groups } = useOfficeScope()
	const { tree } = useCategories()
	// Its own subscription, not a prop: the scope chips label ids from
	// the tenant catalogue, and threading it through as a prop would
	// mean every future caller has to remember to pass it.
	const axes = useAxisCatalog()
	const setF = (patch) => onChange({ ...filters, ...patch })
	return (
		// The table's filters on the card itself, as the fields of the app (no grey box)
		<Box sx={{ pb: 2, mb: 2, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
			<TextField size="small" fullWidth autoFocus
			           placeholder="Search by franchise, tariff, insurance, office, category, date, protection name… (e.g. Carwiz, LED PP, LPA-AP, CDMR, Christmas)"
			           value={filters.search}
			           onChange={(e) => setF({ search: e.target.value })}
			           InputProps={{
			               startAdornment: (
			                   <SearchIcon sx={{ fontSize: 14, color: dsColorNew.textSubtle, mr: 1 }}/>
			               ),
			               endAdornment: filters.search ? (
			                   <IconButton size="small" onClick={() => setF({ search: '' })} aria-label="Clear search"
			                               sx={{ mr: -0.5 }}>
			                       <IconSeeker icon="xmark" sx={{ fontSize: 13, color: dsColorNew.textSubtle }}/>
			                   </IconButton>
			               ) : null,
			           }}
			           sx={{ mb: 1.5 }}/>

			<Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">

				<DateRangeField from={filters.date_from} to={filters.date_to}
				                label="Active between" placeholder="Any dates"
				                cleanable presets={futurePresets()}
				                onChange={({ from, to }) => setF({ date_from: from || '', date_to: to || '' })}/>

				<CompactSelect value={filters.franchise_scope}
				               onChange={(v) => setF({ franchise_scope: v })}
				               options={[
				                   { value: '*', label: 'Any franchise' },
				                   ...franchisesInUse.map(v => ({ value: `franchise:${v}`, label: `Franchise · ${labelForFranchise(axes, v)}` })),
				               ]}/>

				<CompactSelect value={filters.tariff_scope}
				               onChange={(v) => setF({ tariff_scope: v })}
				               options={[
				                   { value: '*', label: 'Any tariff' },
				                   ...tariffsInUse.map(v => ({ value: `tariff:${v}`, label: `Tariff · ${labelForTariff(v)}` })),
				               ]}/>

				<CompactSelect value={filters.insurance_scope}
				               onChange={(v) => setF({ insurance_scope: v })}
				               options={[
				                   { value: '*', label: 'Any insurance' },
				                   ...insurancesInUse.map(v => ({ value: `insurance:${v}`, label: `Insurance · ${labelForInsurance(axes, v)}` })),
				               ]}/>

				<CompactSelect value={filters.office_scope}
				               onChange={(v) => setF({ office_scope: v })}
				               options={[
				                   { value: '*', label: 'Any office' },
				                   ...groups.map(g => ({ value: `group:${g.code}`, label: `Group · ${g.name}` })),
				                   ...offices.map(o => ({ value: `office:${o.code}`, label: `Office · ${o.code}` })),
				               ]}/>

				<CompactSelect value={filters.category_scope}
				               onChange={(v) => setF({ category_scope: v })}
				               options={[
				                   { value: '*', label: 'Any category' },
				                   ...tree.map(m => ({ value: `macro:${m.code}`, label: `Macro · ${m.name}` })),
				               ]}/>

				{/* Every rule is in days now, so filtering by unit would
				    be a control with one answer. "Has a duration scope
				    at all" is still a real question and keeps its two
				    options. */}
				<CompactSelect value={filters.duration_unit}
				               onChange={(v) => setF({ duration_unit: v })}
				               options={[
				                   { value: '*',    label: 'Any duration' },
				                   { value: 'null', label: 'Applies to any (no duration)' },
				                   { value: 'days', label: 'Has a duration range' },
				               ]}/>

				<CompactSelect value={filters.kind}
				               onChange={(v) => setF({ kind: v })}
				               options={[
				                   { value: '*',          label: 'Any kind' },
				                   { value: 'protection', label: 'Date protections' },
				                   { value: 'pickup',     label: 'Pickup-date limits' },
				               ]}/>

				<CompactSelect value={filters.has_price}
				               onChange={(v) => setF({ has_price: v })}
				               options={[
				                   { value: '*',        label: 'Any pricing' },
				                   { value: 'min_only', label: 'Only min' },
				                   { value: 'both',     label: 'Both min + max' },
				               ]}/>

				<Box sx={{ flex: 1 }}/>
				<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>
					{activeCount > 0
					  ? `${filteredTotal} of ${total} shown`
					  : `${total} limit${total === 1 ? '' : 's'}`}
				</Typography>
				{activeCount > 0 && (
					<Button variant="outlined" onClick={() => onChange(EMPTY_FILTERS)}
					        sx={{ ...secondaryButtonSx, height: 32 }}>
						Clear
					</Button>
				)}
			</Stack>
		</Box>
	)
}

function CompactSelect({ value, onChange, options }) {
	return (
		<Select size="small" value={value}
		        onChange={(e) => onChange(e.target.value)}
		        sx={{ minWidth: 150 }}>
			{options.map(o => (
				<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
			))}
		</Select>
	)
}

// The three axes are tenant data now, so a label needs the catalogue.
// Callers pass it in rather than reaching for a module-level list.
function labelForFranchise(axes, key) { return axes.franchiseName(key === '' ? null : Number(key)) }
function labelForTariff(key)          { return key === '1' ? 'Prepaid' : key === '0' ? 'Non prepaid' : key }
function labelForInsurance(axes, key) { return axes.insuranceName(key === '' ? null : Number(key)) }

// ---------------------------------------------------------------------------
// CSV import / export
// ---------------------------------------------------------------------------
const DS_CSV_HEADERS = [
	'franchise', 'tariff_type', 'insurance_type',
	'office_group', 'office', 'macros', 'sipps',
	'duration_unit', 'duration_min', 'duration_max',
	'date_from', 'date_to', 'min_price', 'max_price',
	// CU-869fe8jrk. Without these two, Export → Import "Replace all"
	// would turn every protection into an ordinary limit in silence.
	'protects_dates', 'name',
]
const DS_CSV_TEMPLATE_ROWS = [
	['', '', '', '', '', '', '', '', '', '', '', '', '18', '', '', ''],
	['', '', '', 'airports', '', 'mini', '', 'days', '1', '3', '', '', '22', '', '', ''],
	['canarias', 'prepaid', 'standard', '', 'LPA-AP', '', 'CDMR', 'weeks', '1', '2', '2026-12-20', '2027-01-05', '45', '', '', ''],
	['', '', '', '', 'TFS-AP', '', 'FVAR', '', '', '', '', '', '55', '200', '', ''],
	['', '', '', '', '', '', '', '', '', '', '2026-12-26', '2026-12-31', '50', '', 'yes', 'Christmas'],
]
// RateGain / Carwiz format (real partner export).
const RATEGAIN_HEADERS = [
	'Brand', 'PickupDateStart', 'PickupDateEnd',
	'PickupLocation', 'DropOffLocation',
	'ProductCode', 'CarCode', 'LOR', 'Min', 'Max', 'Acceptance',
]

function toCsv(limits) {
	const rows = [DS_CSV_HEADERS.join(',')]
	for (const l of limits) {
		const macros = (l.scope_categories || [])
			.filter(c => c.type === 'macro').map(c => c.code).join('|')
		const sipps  = (l.scope_categories || [])
			.filter(c => c.type === 'sipp').map(c => c.code).join('|')
		rows.push([
			// Ids, not names: an export that round-trips through the
			// importer has to carry something the importer can resolve
			// without guessing, and a brand can be renamed.
			csvCell(l.scope_ps_provider_id),
			csvCell(l.scope_is_prepaid == null ? '' : (l.scope_is_prepaid ? 'prepaid' : 'postpaid')),
			csvCell(l.scope_ps_insurance_id),
			csvCell(l.scope_office_group),
			csvCell((l.scope_offices || []).join('|')),
			csvCell(macros),
			csvCell(sipps),
			csvCell(l.scope_duration ? 'days' : ''),
			csvCell(l.scope_duration?.min),
			csvCell(l.scope_duration?.max),
			csvCell(l.date_from),
			csvCell(l.date_to),
			csvCell(l.min_price),
			csvCell(l.max_price),
			csvCell(l.protects_dates ? 'yes' : ''),
			csvCell(l.protects_dates ? l.name : ''),
		].join(','))
	}
	return rows.join('\n')
}

function csvCell(v) {
	if (v == null || v === '') return ''
	const s = String(v)
	return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function downloadCsv(limits) {
	const csv = toCsv(limits)
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = `price-limits-${new Date().toISOString().slice(0, 10)}.csv`
	document.body.appendChild(a)
	a.click()
	document.body.removeChild(a)
	URL.revokeObjectURL(url)
}

function detectCsvFormat(text) {
	const first = (text.split(/\r?\n/, 1)[0] || '').toLowerCase()
	const isRg = first.includes('brand') && first.includes('pickupdatestart')
	          && first.includes('carcode') && first.includes('lor')
	return isRg ? 'rategain' : 'ds'
}

// `axes` is threaded all the way down because a CSV names brands and
// products in words and the model stores Price Seeker ids. A word that
// resolves to nothing becomes "any" plus a warning: a price limit
// scoped to the WRONG brand is worse than one scoped to all of them.
function parseCsv(text, axes) {
	const fmt = detectCsvFormat(text)
	const parsed = fmt === 'rategain' ? parseRateGain(text, axes) : parseDs(text, axes)
	return { format: fmt, ...parsed }
}

function parseDs(text, axes) {
	const unresolved = new Map()
	// Records a value that could not be resolved and returns the
	// resolution anyway, so an unknown brand widens the scope instead of
	// aborting the whole import.
	const keep = (field, raw, resolved) => {
		if (resolved == null && String(raw || '').trim()) {
			unresolved.set(`${field}=${raw}`, field)
		}
		return resolved
	}
	const lines = text.split(/\r?\n/).filter(l => l.trim().length)
	if (!lines.length) return { rows: [], errors: ['File is empty.'], warnings: [] }
	const headers = splitCsvLine(lines[0])
	// Everything is optional except min_price + max_price (checked per-row).
	const rows = []
	const errors = []
	for (let i = 1; i < lines.length; i++) {
		const cells = splitCsvLine(lines[i])
		const rec = Object.fromEntries(headers.map((h, idx) => [h, cells[idx] ?? '']))
		const macros = rec.macros ? rec.macros.split(/[|;]/).filter(Boolean) : []
		const sipps  = rec.sipps  ? rec.sipps .split(/[|;]/).filter(Boolean) : []
		const scope_categories = [
			...macros.map(c => ({ type: 'macro', code: c.trim() })),
			...sipps .map(c => ({ type: 'sipp',  code: c.trim() })),
		]
		// `duration_unit` stays in the header so files exported before
		// 2026-09-30 still import, but whatever it says is ignored:
		// the engine never read it and a file claiming weeks was being
		// matched in days anyway. A range with no unit is a range in days.
		const hasDuration = rec.duration_min !== '' || rec.duration_max !== ''
		const scope_duration = hasDuration
			? { unit: 'days',
			    min: rec.duration_min === '' ? null : Number(rec.duration_min),
			    max: rec.duration_max === '' ? null : Number(rec.duration_max) }
			: null
		const minP = rec.min_price === '' ? null : Number(rec.min_price)
		const maxP = rec.max_price === '' ? null : Number(rec.max_price)
		if (minP == null && maxP == null) {
			errors.push(`Row ${i + 1}: need at least min_price or max_price.`)
			continue
		}
		rows.push({
			id: uid(),
			scope_ps_provider_id:  keep('franchise', rec.franchise,
			                            resolveFranchise(axes, rec.franchise)),
			scope_is_prepaid:      keep('tariff_type', rec.tariff_type,
			                            resolvePrepaid(rec.tariff_type)),
			scope_ps_insurance_id: keep('insurance_type', rec.insurance_type,
			                            resolveInsurance(axes, rec.insurance_type)),
			scope_office_group: rec.office_group || null,
			scope_offices:      rec.office ? rec.office.split('|').map(s => s.trim()).filter(Boolean) : null,
			scope_categories:   scope_categories.length ? scope_categories : null,
			scope_duration,
			date_from: rec.date_from || null,
			date_to:   rec.date_to   || null,
			protects_dates: /^(yes|true|1)$/i.test((rec.protects_dates || '').trim()),
			name: (rec.name || '').trim() || null,
			min_price: minP,
			max_price: maxP,
		})
	}
	const warnings = []
	if (unresolved.size) {
		warnings.push(
			`Could not match ${[...unresolved.keys()].join(', ')} to this tenant's ` +
			`catalogue — those rules were imported with that scope left as "any". ` +
			`Set them by hand, or export from this screen to get the ids.`)
	}
	return { rows, errors, warnings }
}

/**
 * Parse a RateGain / Carwiz export.
 *
 *   Brand         → scope_ps_provider_id (matched by name against the
 *                   tenant's own brands; no match = any, with a warning)
 *   ProductCode   → scope_is_prepaid (read as a word: prepaid/postpaid)
 *   (insurance)   → not present in RateGain export → left null + warning
 *   PickupLocation→ scope_offices (one code per source row)
 *   CarCode       → scope_categories: [{type:'sipp', code}]
 *   LOR           → duration range (breakpoint expansion — see below)
 *   Min / Max     → min_price / max_price
 *   Acceptance    → dropped (not in our model)
 *
 * LOR breakpoints: RateGain lists discrete anchors (1, 3, 5, 7, 10, 14,
 * 21, 28, 31…) each with its own Min/Max. In our model that becomes a
 * duration range. We expand each breakpoint to cover the interval up to
 * the NEXT breakpoint minus one day, in the context of the SAME
 * (Brand, ProductCode, PickupLocation, DropOffLocation, CarCode,
 * PickupDateStart, PickupDateEnd) group. The last breakpoint of a group
 * becomes open-ended (max: null).
 */
function parseRateGain(text, axes) {
	const lines = text.split(/\r?\n/).filter(l => l.trim().length)
	if (!lines.length) return { rows: [], errors: ['File is empty.'], warnings: [] }
	const headers = splitCsvLine(lines[0])
	const missing = RATEGAIN_HEADERS.filter(h => !headers.includes(h))
	if (missing.length) {
		return { rows: [], errors: [`Missing RateGain columns: ${missing.join(', ')}`], warnings: [] }
	}

	const raw = []
	const errors = []
	const warnings = []
	const unresolvedRg = new Map()
	const keepRg = (field, rawValue, resolved) => {
		if (resolved == null && String(rawValue || '').trim()) {
			unresolvedRg.set(`${field}=${rawValue}`, field)
		}
		return resolved
	}
	let differentDropoff = 0
	const droppedFields = new Set()

	for (let i = 1; i < lines.length; i++) {
		const cells = splitCsvLine(lines[i])
		const rec = Object.fromEntries(headers.map((h, idx) => [h, cells[idx] ?? '']))
		const df = parseDdmmyyyy(rec.PickupDateStart)
		const dt = parseDdmmyyyy(rec.PickupDateEnd)
		if (rec.PickupDateStart && !df) errors.push(`Row ${i + 1}: bad PickupDateStart "${rec.PickupDateStart}".`)
		if (rec.PickupDateEnd   && !dt) errors.push(`Row ${i + 1}: bad PickupDateEnd "${rec.PickupDateEnd}".`)
		const lor = Number(rec.LOR)
		if (!Number.isFinite(lor) || lor <= 0) {
			errors.push(`Row ${i + 1}: LOR "${rec.LOR}" is not a positive number.`)
			continue
		}
		const minP = rec.Min === '' ? null : Number(rec.Min)
		const maxP = rec.Max === '' ? null : Number(rec.Max)
		if (minP == null && maxP == null) {
			errors.push(`Row ${i + 1}: need at least Min or Max.`)
			continue
		}
		if (rec.PickupLocation && rec.DropOffLocation
		    && rec.PickupLocation !== rec.DropOffLocation) {
			differentDropoff++
		}
		if (rec.Acceptance && rec.Acceptance !== '100') droppedFields.add('Acceptance')

		raw.push({
			franchise:  rec.Brand,
			tariff:     rec.ProductCode,
			office:     rec.PickupLocation,
			sipp:       rec.CarCode,
			date_from:  df,
			date_to:    dt,
			lor,
			min: minP,
			max: maxP,
		})
	}

	// Group by (franchise, tariff, office, sipp, date_from, date_to) so
	// LOR breakpoints inside the same group become a contiguous range set.
	const groups = new Map()
	for (const r of raw) {
		const k = [r.franchise, r.tariff, r.office, r.sipp, r.date_from, r.date_to].join('|')
		if (!groups.has(k)) groups.set(k, [])
		groups.get(k).push(r)
	}

	const rows = []
	for (const list of groups.values()) {
		list.sort((a, b) => a.lor - b.lor)
		for (let i = 0; i < list.length; i++) {
			const cur  = list[i]
			const next = list[i + 1]
			const min  = cur.lor
			const max  = next ? next.lor - 1 : null   // open-ended for the tail
			rows.push({
				id: uid(),
				scope_ps_provider_id:  keepRg('Brand', cur.franchise,
				                              resolveFranchise(axes, cur.franchise)),
				scope_is_prepaid:      keepRg('ProductCode', cur.tariff,
				                              resolvePrepaid(cur.tariff)),
				scope_ps_insurance_id: null,
				scope_office_group: null,
				scope_offices:      cur.office ? [cur.office] : null,
				scope_categories:   cur.sipp ? [{ type: 'sipp', code: cur.sipp }] : null,
				scope_duration: { unit: 'days', min, max },
				date_from: cur.date_from || null,
				date_to:   cur.date_to   || null,
				min_price: cur.min,
				max_price: cur.max,
			})
		}
	}

	if (unresolvedRg.size) {
		warnings.push(
			`Could not match ${[...unresolvedRg.keys()].join(', ')} to this tenant's ` +
			`catalogue — those rules were imported with that scope left as "any".`)
	}
	warnings.push(`Insurance type is not part of the RateGain export — every imported rule has an empty insurance scope (i.e. applies to any). Add it by hand if you need per-insurance limits.`)
	if (differentDropoff > 0) {
		warnings.push(`${differentDropoff} row${differentDropoff === 1 ? '' : 's'} had a different DropOffLocation — kept only PickupLocation (one-office model).`)
	}
	if (droppedFields.size > 0) {
		warnings.push(`Ignored fields: ${[...droppedFields].join(', ')} — not part of our model yet.`)
	}
	warnings.push(`LOR breakpoints expanded into contiguous day ranges (LOR=N covers N up to the next breakpoint − 1).`)

	return { rows, errors, warnings }
}

function parseDdmmyyyy(s) {
	if (!s) return null
	const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s.trim())
	if (!m) return null
	return `${m[3]}-${m[2]}-${m[1]}`
}

function splitCsvLine(line) {
	const out = []
	let cur = ''
	let inQ = false
	for (let i = 0; i < line.length; i++) {
		const c = line[i]
		if (inQ) {
			if (c === '"' && line[i + 1] === '"') { cur += '"'; i++ }
			else if (c === '"') inQ = false
			else cur += c
		} else {
			if (c === ',') { out.push(cur); cur = '' }
			else if (c === '"' && !cur) inQ = true
			else cur += c
		}
	}
	out.push(cur)
	return out.map(s => s.trim())
}

function CsvImportDialog({ open, onClose, onImport }) {
	const [text, setText] = useState('')
	const [mode, setMode] = useState('append')
	const fileInputRef = useRef(null)
	const axes = useAxisCatalog()
	const parsed = useMemo(() =>
		text ? parseCsv(text, axes) : { rows: [], errors: [], warnings: [], format: null },
	[text, axes])

	function onFile(e) {
		const f = e.target.files?.[0]
		if (!f) return
		const reader = new FileReader()
		reader.onload = () => setText(reader.result)
		reader.readAsText(f)
	}
	function downloadTemplate() {
		const csv = [DS_CSV_HEADERS.join(','),
		             ...DS_CSV_TEMPLATE_ROWS.map(r => r.join(','))].join('\n')
		const blob = new Blob([csv], { type: 'text/csv' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url; a.download = 'price-limits-template.csv'
		a.click(); URL.revokeObjectURL(url)
	}

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="md" paperSx={setupScopeSx}>
			<AppDialogHeader icon="file-import" title="Import price limits from CSV" onClose={onClose}/>
			<AppDialogBody sx={dialogBodySx}>
				<Stack gap={2}>
					<Box sx={{ fontSize: 13 }}>
						Two CSV formats are auto-detected:
						<Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
							<li><strong>DS native</strong> — same taxonomy as Rate Cards:
								<Box component="code" sx={{ display: 'block', mt: 0.5,
								                            fontFamily: 'inherit',
								                            fontSize: 11.5,
								                            bgcolor: dsColorNew.white, px: 0.75, py: 0.5,
								                            borderRadius: '6px', border: '1px solid',
								                            borderColor: dsColorNew.grayBorder,
								                            wordBreak: 'break-all' }}>
									{DS_CSV_HEADERS.join(', ')}
								</Box>
							</li>
							<li><strong>RateGain / Carwiz export</strong> — the real partner
								file. <code>Brand→franchise</code>, <code>ProductCode→tariff_type</code>,
								LOR breakpoints expand into day ranges automatically. Insurance
								comes empty (RateGain doesn't ship it).</li>
						</Box>
					</Box>

					<Stack direction="row" gap={1} alignItems="center">
						<input ref={fileInputRef}
						       type="file" accept=".csv,text/csv"
						       onChange={onFile}
						       style={{ display: 'none' }}/>
						<Button variant="outlined" startIcon={<UploadIcon/>}
						        onClick={() => fileInputRef.current?.click()}
						        sx={{ ...secondaryButtonSx, height: 36 }}>
							Choose file
						</Button>
						<Button variant="text" startIcon={<DownloadIcon/>}
						        onClick={downloadTemplate}>
							Download DS template
						</Button>
					</Stack>

					<TextField multiline minRows={6} maxRows={12}
					           placeholder="…or paste CSV content here"
					           value={text}
					           onChange={(e) => setText(e.target.value)}
					           sx={{ bgcolor: dsColorNew.white, ...inputSx,
					                 '& .MuiOutlinedInput-input': {
					                     fontFamily: 'inherit',
					                     fontSize: 12,
					                 } }}/>

					{parsed.format && (
						<AppNote severity="info">
							<strong>Detected format:</strong>{' '}
							{parsed.format === 'rategain'
							  ? 'RateGain / Carwiz export'
							  : 'DS native template'}
						</AppNote>
					)}

					{parsed.errors.length > 0 && (
						<AppNote severity="error">
							<Typography sx={{ fontWeight: 600, color: dsColorNew.dangerRed,
							                  fontSize: 13, mb: 0.5 }}>
								{parsed.errors.length} error{parsed.errors.length === 1 ? '' : 's'} to fix:
							</Typography>
							{parsed.errors.slice(0, 5).map((e, i) => (
								<Typography key={i} sx={{ fontSize: 12, color: dsColorNew.dangerRed }}>
									• {e}
								</Typography>
							))}
							{parsed.errors.length > 5 && (
								<Typography sx={{ fontSize: 12, color: dsColorNew.dangerRed, mt: 0.5 }}>
									…and {parsed.errors.length - 5} more.
								</Typography>
							)}
						</AppNote>
					)}

					{parsed.warnings?.length > 0 && (
						<AppNote severity="warning">
							<Typography sx={{ fontWeight: 600, color: dsColorNew.noteAmber,
							                  fontSize: 13, mb: 0.5 }}>
								Notes:
							</Typography>
							{parsed.warnings.map((w, i) => (
								<Typography key={i} sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>
									• {w}
								</Typography>
							))}
						</AppNote>
					)}

					{parsed.rows.length > 0 && (
						<AppNote severity="success">
							<Typography sx={{ fontWeight: 600, color: dsColorNew.successText,
							                  fontSize: 13 }}>
								{parsed.rows.length} rule{parsed.rows.length === 1 ? '' : 's'} ready to import.
							</Typography>
						</AppNote>
					)}

					<FormControlLabel
						control={<Switch checked={mode === 'replace'}
						                 onChange={(e) => setMode(e.target.checked ? 'replace' : 'append')}/>}
						label={<Typography sx={{ fontSize: 13 }}>
						  <strong>{mode === 'replace' ? 'Replace all' : 'Append to existing'}</strong>
						  {' '}—{' '}
						  {mode === 'replace'
						    ? 'wipes every current limit and loads the CSV from scratch.'
						    : 'keeps current limits and adds the CSV on top.'}
						</Typography>}/>
				</Stack>
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}/>
				<AppBulkEditAction disabled={parsed.rows.length === 0}
				                   onClick={() => onImport(parsed.rows, mode)}>
					Import {parsed.rows.length || ''} rule{parsed.rows.length === 1 ? '' : 's'}
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}

// ---------------------------------------------------------------------------
// Filter application
// ---------------------------------------------------------------------------
function applyFilters(rules, f, axes) {
	const filterFrom = f.date_from ? f.date_from : null
	const filterTo   = f.date_to   ? f.date_to   : null
	return rules.filter(r => {
		// Date-range overlap: a rule with null dates ("applies always") is
		// counted as overlapping any filter range. Otherwise both endpoints
		// are treated as inclusive; an unbounded rule endpoint counts as
		// −∞ / +∞ respectively.
		if (filterFrom || filterTo) {
			const rFrom = r.date_from || null
			const rTo   = r.date_to   || null
			const isAlwaysActive = !rFrom && !rTo
			if (!isAlwaysActive) {
				if (filterFrom && rTo   && rTo   < filterFrom) return false
				if (filterTo   && rFrom && rFrom > filterTo)   return false
			}
		}
		if (f.search) {
			const hay = [
				axes.franchiseName(r.scope_ps_provider_id),
				axes.prepaidName(r.scope_is_prepaid),
				axes.insuranceName(r.scope_ps_insurance_id),
				r.scope_office_group, ...(r.scope_offices || []),
				...(r.scope_categories || []).map(c => c.code),
				r.date_from, r.date_to, r.name,
			].filter(Boolean).join(' ').toLowerCase()
			if (!hay.includes(f.search.toLowerCase())) return false
		}
		// Compared as keys, never as values: `scope_is_prepaid === false`
		// has to survive the comparison, and `'' !== null` would drop it.
		if (f.franchise_scope !== '*') {
			const key = f.franchise_scope.slice('franchise:'.length)
			if (idKey(r.scope_ps_provider_id) !== key) return false
		}
		if (f.tariff_scope !== '*') {
			const key = f.tariff_scope.slice('tariff:'.length)
			if (prepaidKey(r.scope_is_prepaid) !== key) return false
		}
		if (f.insurance_scope !== '*') {
			const key = f.insurance_scope.slice('insurance:'.length)
			if (idKey(r.scope_ps_insurance_id) !== key) return false
		}
		if (f.office_scope !== '*') {
			const [kind, code] = f.office_scope.split(':')
			if (kind === 'office' && !(r.scope_offices || []).includes(code)) return false
			if (kind === 'group'  && r.scope_office_group !== code) return false
		}
		if (f.category_scope !== '*') {
			const [kind, code] = f.category_scope.split(':')
			const cats = r.scope_categories || []
			if (kind === 'macro' && !cats.some(c => c.type === 'macro' && c.code === code)) return false
			if (kind === 'sipp'  && !cats.some(c => c.type === 'sipp'  && c.code === code)) return false
		}
		if (f.duration_unit !== '*') {
			// The question is whether the rule scopes duration at all,
			// not in which unit — there is only one unit now.
			if (f.duration_unit === 'null' && r.scope_duration) return false
			if (f.duration_unit === 'days' && !r.scope_duration) return false
		}
		if (f.kind === 'protection' && !r.protects_dates) return false
		if (f.kind === 'pickup'     &&  r.protects_dates) return false
		if (f.has_price === 'min_only' && r.max_price != null) return false
		if (f.has_price === 'both'     && (r.min_price == null || r.max_price == null)) return false
		return true
	})
}

// ---------------------------------------------------------------------------
// Specificity + shared components
// ---------------------------------------------------------------------------
function specificity(r) {
	const catScore = r.scope_categories?.length
		? (r.scope_categories.some(c => c.type === 'sipp') ? 2 : 1)
		: 0
	// `!= null` and not truthiness: a limit scoped to Non Prepaid
	// (`false`) is scoped, and counting it as unscoped is the bug that
	// let a general limit beat a specific one on the server.
	return (r.scope_ps_provider_id  != null ? 1 : 0)
	     + (r.scope_is_prepaid      != null ? 1 : 0)
	     + (r.scope_ps_insurance_id != null ? 1 : 0)
	     + (r.scope_office_group   ? 1 : 0)
	     + (r.scope_offices?.length ? 2 : 0)
	     + catScore
	     + (r.scope_duration       ? 1 : 0)
	     + ((r.date_from || r.date_to) ? 1 : 0)
}

// Three Rate-Card-style enum selects stacked in one cell. Same look as
// Rate Cards' EnumSelect (chip-based renderValue with coloured palettes,
// dashed "All …" placeholder chip when empty). Values imported from the
// RateGain CSV that don't live in the catalog (e.g. "LED PP" as a tariff)
// are appended as dynamic options with an "(imported)" tag so they stay
// selectable instead of falling out of the model on next open.
function TaxonomyPicker({ rule, onChange }) {
	const axes = useAxisCatalog()
	return (
		<Stack gap={0.5}>
			<EnumScopeSelect value={idKey(rule.scope_ps_provider_id)}
			                 onChange={(v) => onChange({ scope_ps_provider_id: idValue(v) })}
			                 options={axes.franchises}
			                 allLabel="All franchises"
			                 variant="franchise"/>
			<EnumScopeSelect value={prepaidKey(rule.scope_is_prepaid)}
			                 onChange={(v) => onChange({ scope_is_prepaid: prepaidValue(v) })}
			                 options={PREPAID_OPTIONS}
			                 allLabel="All tariff types"
			                 variant="tariff"/>
			<EnumScopeSelect value={idKey(rule.scope_ps_insurance_id)}
			                 onChange={(v) => onChange({ scope_ps_insurance_id: idValue(v) })}
			                 options={axes.insurances}
			                 allLabel="All insurance"
			                 variant="insurance"/>
		</Stack>
	)
}

// Same visual language as Rate Cards' EnumSelect, but tolerates values
// not present in `options` (imported from CSV) by rendering them as
// custom chips + injecting a dynamic MenuItem.
function EnumScopeSelect({ value, options, onChange, variant, allLabel }) {
	const palette = {
		site:      { bg: dsColorNew.pillActiveBg, fg: dsColorNew.appBlue, border: dsColorNew.noteInfoBorder },
		tariff:    { bg: dsColorNew.noteInfoBg, fg: dsColorNew.noteInfo, border: dsColorNew.noteInfoBorder },
		insurance: { bg: dsColorNew.noteAmberBg, fg: dsColorNew.noteAmber, border: dsColorNew.noteAmberBorder },
		franchise: { bg: dsColorNew.pillTrackBg, fg: dsColorNew.textDark, border: dsColorNew.grayBorder },
	}[variant] || { bg: dsColorNew.pillTrackBg, fg: dsColorNew.textDark, border: dsColorNew.grayBorder }
	const isCustom = value && !options.some(o => o.code === value)
	return (
		<Select size="small" fullWidth displayEmpty
		        value={value ?? ''}
		        onChange={(e) => onChange(e.target.value || null)}
		        renderValue={(v) => {
		          if (!v) return (
		            <Chip size="small"
		                  label={allLabel}
		                  sx={{ height: 22, fontWeight: 700, fontSize: 11,
		                        letterSpacing: '0.02em',
		                        maxWidth: '100%',
		                        bgcolor: dsColorNew.white,
		                        color: dsColorNew.textMuted,
		                        border: '1px dashed', borderColor: dsColorNew.textSubtle,
		                        fontStyle: 'italic',
		                        '& .MuiChip-label': {
		                            whiteSpace: 'nowrap',
		                            overflow: 'hidden',
		                            textOverflow: 'ellipsis',
		                        } }}/>
		          )
		          const opt = options.find(o => o.code === v)
		          return (
		            <Chip size="small"
		                  label={opt?.name || v}
		                  sx={{ height: 22, fontWeight: 700, fontSize: 11,
		                        maxWidth: '100%',
		                        bgcolor: palette.bg, color: palette.fg,
		                        border: '1px solid', borderColor: palette.border,
		                        fontFamily: opt ? 'inherit' : 'inherit',
		                        '& .MuiChip-label': {
		                            whiteSpace: 'nowrap',
		                            overflow: 'hidden',
		                            textOverflow: 'ellipsis',
		                        } }}/>
		          )
		        }}
		        sx={inputSx}>
			<MenuItem value="">
				<em style={{ color: dsColorNew.textMuted }}>{allLabel}</em>
			</MenuItem>
			{options.map(o => (
				<MenuItem key={o.code} value={o.code}>{o.name}</MenuItem>
			))}
			{isCustom && (
				<MenuItem value={value}>
					<Box component="code" sx={{ mr: 1, fontWeight: 700,
					                            fontFamily: 'inherit' }}>
						{value}
					</Box>
					<em style={{ color: dsColorNew.textMuted }}>(imported)</em>
				</MenuItem>
			)}
		</Select>
	)
}

/**
 * Any office, a group, or one office — typed, not hunted for.
 *
 * It was a `Select`, which offers no way to search: a tenant with a
 * hundred locations meant scrolling a menu to find `TFS-AP` (Curro,
 * 2026-09-29). An Autocomplete over the same two groups takes a few
 * keystrokes instead, and matches the code as well as the name —
 * people know the code.
 */
function OfficeScopePicker({ rule, onChange }) {
	// `dim_location`, not the six invented airports. This tenant's
	// office is `A1`; the mock offered none of it (Curro, 2026-09-28).
	const { offices, groups } = useOfficeScope()
	const chosen = rule.scope_offices || []

	const options = useMemo(() => {
		const out = []
		for (const g of groups) {
			out.push({ key: `group:${g.code}`, label: g.name, code: g.code, group: 'Office groups' })
		}
		for (const o of offices) {
			out.push({ key: `office:${o.code}`, label: o.name || o.code, code: o.code, group: 'Offices' })
		}
		// Codes from a CSV import the catalogue does not know. Dropping
		// them would silently rescope the rule on open.
		for (const code of chosen) {
			if (!offices.some(o => o.code === code)) {
				out.push({ key: `office:${code}`, code, label: 'imported', group: 'Offices' })
			}
		}
		return out
	}, [offices, groups, chosen.join('|')])

	// One group, or any number of offices, or nothing — never both. A
	// group names a set somebody else maintains; offices spell it out.
	const value = rule.scope_office_group
		? options.filter(o => o.key === `group:${rule.scope_office_group}`)
		: options.filter(o => o.key.startsWith('office:') && chosen.includes(o.code))

	return (
		<Autocomplete size="small" fullWidth multiple disableCloseOnSelect
		              options={options}
		              value={value}
		              groupBy={(o) => o.group}
		              isOptionEqualToValue={(a, b) => a.key === b.key}
		              getOptionLabel={(o) => (o.code ? `${o.code} · ${o.label}` : o.label)}
		              filterOptions={(opts, { inputValue }) => {
		                  const q = inputValue.trim().toLowerCase()
		                  if (!q) return opts
		                  return opts.filter(o => `${o.code || ''} ${o.label}`.toLowerCase().includes(q))
		              }}
		              onChange={(_, picked) => {
		                  const last = picked[picked.length - 1]
		                  if (!last) return onChange({ scope_offices: null, scope_office_group: null })
		                  // The newest pick decides the kind; the other kind is dropped.
		                  if (last.key.startsWith('group:')) {
		                      return onChange({ scope_offices: null, scope_office_group: last.code })
		                  }
		                  const codes = picked.filter(o => o.key.startsWith('office:')).map(o => o.code)
		                  return onChange({ scope_offices: codes.length ? codes : null, scope_office_group: null })
		              }}
		              renderTags={(sel, getTagProps) => sel.map((o, i) => (
		                  <Chip {...getTagProps({ index: i })} key={o.key} size="small"
		                        label={o.key.startsWith('group:') ? `${o.label} (group)` : o.code}
		                        sx={{ fontWeight: 700, fontFamily: o.key.startsWith('group:') ? 'inherit' : 'inherit' }}/>
		              ))}
		              renderOption={(props, o) => (
		                  <li {...props} key={o.key}>
		                      {o.code && (
		                          <Box component="code" sx={{ mr: 1, fontWeight: 700,
		                                                      fontFamily: 'inherit' }}>
		                              {o.code}
		                          </Box>
		                      )}
		                      <Typography sx={{ fontSize: 13 }}>{o.label}</Typography>
		                  </li>
		              )}
		              renderInput={(params) => (
		                  <TextField {...params} sx={inputSx}
		                             placeholder={value.length ? '' : 'Any office — or pick offices / a group…'}/>
		              )}/>
	)
}

/** Bring a linked-to row into view, once, as it mounts. */
const scrollIntoView = (el) =>
	el?.scrollIntoView({ block: 'center', behavior: 'smooth' })

function CategoryScopePicker({ value, onChange }) {
	const { tree } = useCategories()
	const options = useMemo(() => {
		const opts = []
		for (const m of tree) {
			opts.push({ type: 'macro', code: m.code, label: `All ${m.name}`, group: m.name })
			for (const s of m.sipps) {
				opts.push({ type: 'sipp', code: s, label: s, group: m.name })
			}
		}
		return opts
	}, [tree])
	const optionsWithCustom = useMemo(() => {
		if (!value?.length) return options
		const known = new Set(options.map(o => `${o.type}:${o.code}`))
		const extra = value
			.filter(v => !known.has(`${v.type}:${v.code}`))
			.map(v => ({ type: v.type, code: v.code, label: v.code, group: '(imported)' }))
		return [...options, ...extra]
	}, [options, value])
	const selected = useMemo(() => {
		if (!value?.length) return []
		return optionsWithCustom.filter(o => value.some(v => v.type === o.type && v.code === o.code))
	}, [value, optionsWithCustom])
	return (
		<Autocomplete multiple size="small"
		              options={optionsWithCustom}
		              value={selected}
		              disableCloseOnSelect
		              groupBy={(o) => o.group}
		              getOptionLabel={(o) => o.label}
		              isOptionEqualToValue={(a, b) => a.type === b.type && a.code === b.code}
		              onChange={(_, next) =>
		                onChange(next.length
		                  ? next.map(o => ({ type: o.type, code: o.code }))
		                  : null)}
		              renderTags={(v, getTagProps) =>
		                v.map((o, idx) => (
		                  <Chip {...getTagProps({ index: idx })}
		                        key={`${o.type}:${o.code}`} size="small"
		                        label={o.type === 'macro' ? o.label : o.code}
		                        sx={{
		                            fontFamily: o.type === 'sipp' ? 'inherit' : 'inherit',
		                            fontWeight: 700, fontSize: 11,
		                            bgcolor: o.type === 'macro' ? dsColorNew.pillActiveBg : dsColorNew.noteInfoBg,
		                            color:   o.type === 'macro' ? dsColorNew.appBlue : dsColorNew.appBlue,
		                            border: '1px solid',
		                            borderColor: o.type === 'macro' ? alpha(dsColorNew.appBlue, 0.45) : dsColorNew.noteInfoBorder,
		                        }}/>
		                ))}
		              renderOption={(props, o, { selected }) => (
		                <li {...props} key={`${o.type}:${o.code}`}
		                    style={{
		                      ...props.style,
		                      paddingLeft: o.type === 'macro' ? 16 : 32,
		                      fontWeight: o.type === 'macro' ? 700 : 500,
		                      fontFamily: o.type === 'sipp' ? 'inherit' : 'inherit',
		                      background: selected ? dsColorNew.hoverBgLight : undefined,
		                    }}>
		                  {o.type === 'macro'
		                    ? <span style={{ color: dsColorNew.appBlue }}>{o.label}</span>
		                    : o.code}
		                </li>
		              )}
		              renderGroup={(params) => (
		                <li key={params.key}>
		                  <Box sx={{
		                      px: 1.5, py: 0.5,
		                      bgcolor: dsColorNew.pillTrackBg,
		                      fontSize: 11, fontWeight: 700,
		                      textTransform: 'uppercase', letterSpacing: '0.06em',
		                      color: dsColorNew.textMuted,
		                      position: 'sticky', top: -8, zIndex: 1,
		                  }}>
		                    {params.group}
		                  </Box>
		                  <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
		                </li>
		              )}
		              renderInput={(params) => (
		                <TextField {...params}
		                           placeholder={selected.length ? '' : 'Any category'}
		                           sx={inputSx}/>
		              )}/>
	)
}

/** One header row per kind of limit, so the five merged cells of a
 *  binding are read against their own caption and not against the
 *  "Category scope … Max €/day" headings above them. */
function GroupHeaderRow({ kind, count }) {
	const text = kind === 'protection'
		? `Date protections · ${count} — a floor for every rental of up to 30 days that crosses these dates, whatever its pickup date. Added on top of the rules below: the highest floor wins, specificity does not apply. Duration bands are prices, not filters.`
		: kind === 'matrix'
		? `Matrices · ${count} — floors per ACRISS and rental days, bound to a scope and a date range`
		: `Individual limits · ${count} — one floor, and an optional ceiling, per scope`
	return (
		<TableRow>
			<TableCell colSpan={10}
			           sx={{ bgcolor: dsColorNew.pillTrackBg, color: dsColorNew.textMuted, py: 0.6, px: 1.5,
			                 fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em',
			                 textTransform: 'uppercase', borderBottom: '1px solid',
			                 borderColor: dsColorNew.grayBorder }}>
				{text}
			</TableCell>
		</TableRow>
	)
}

/** `⛨ PROTECTION`, the geometry of `MATRIX` in a colour the tab does not
 *  use for warnings or matrices (UX 3, declared deviation). */
function ProtectionChip() {
	return (
		<Chip size="small" icon={<ShieldIcon sx={{ fontSize: 13, color: 'inherit !important' }}/>}
		      label="PROTECTION"
		      sx={{ height: 22, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', borderRadius: '6px',
		            bgcolor: dsColorNew.noteAmberBg, color: dsColorNew.noteAmber, '& .MuiChip-icon': { ml: 0.5, color: 'inherit' } }}/>
	)
}

/**
 * The second reading of the row's two dates (CU-869fe8jrk). Off: the
 * floor applies when the pickup date is within the range. On: to any
 * rental of up to 30 days that overlaps it — the whole rental, at
 * this floor — and the protection can be named for the Activity.
 */
function DateModeToggle({ row, onToggle, onName }) {
	const on = !!row.protects_dates
	return (
		<Stack gap={0.25}>
			<Stack direction="row" alignItems="center" gap={0.5}
			       title="Off: the floor applies when the pickup date is within the range. On: it applies to any rental of up to 30 days that overlaps the range — the whole rental, at this floor.">
				<AppAntSwitch checked={on} onChange={(e) => onToggle(e.target.checked)}/>
				<Typography sx={{ fontSize: 12, fontWeight: on ? 600 : 400,
				                  color: on ? dsColorNew.textTitle : dsColorNew.textMuted }}>
					Protect these dates
				</Typography>
			</Stack>
			{on && (
				<>
					<Typography sx={{ fontSize: 11.5, color: dsColorNew.textMuted, lineHeight: 1.4 }}>
						Floor for any rental of up to 30 days that crosses them, whatever its pickup date.
					</Typography>
					<TextField size="small" value={row.name ?? ''} placeholder="Name, e.g. Christmas"
					           onChange={(e) => onName(e.target.value || null)}
					           inputProps={{ style: { width: 160 } }} sx={inputSx}/>
					{!(row.date_from && row.date_to) && (
						<Typography sx={{ fontSize: 11.5, color: dsColorNew.noteAmber }}>
							A protection needs the dates it protects.
						</Typography>
					)}
				</>
			)}
		</Stack>
	)
}

/**
 * Bands on a protection are prices, never a filter — but a bounded
 * duration still leaves longer rentals that cross unprotected, and the
 * operator may not have meant that (UX 6, brief criterion 8). Inline and
 * persistent: Setup saves on blur and a toast would be gone.
 */
function ProtectionDurationWarning({ row, all, matrices }) {
	if (!row.protects_dates) return null
	let text = null
	if (row.matrix_id) {
		const m = (matrices || []).find(x => x.id === row.matrix_id)
		const last = m?.base?.[m.base.length - 1]
		if (m && last && last.to != null) {
			text = `“${m.name}” prices rentals up to ${last.to} days — longer rentals cross ${row.name || 'these dates'} unprotected. Add an open-ended band to the matrix, or accept it.`
		}
	} else if (row.scope_duration?.max != null) {
		const next = (all || []).some(o => o !== row && o.protects_dates
			&& (o.name || '') === (row.name || '') && o.date_from === row.date_from && o.date_to === row.date_to
			&& o.scope_duration?.min != null && o.scope_duration.min <= row.scope_duration.max + 1
			&& (o.scope_duration.max == null || o.scope_duration.max > row.scope_duration.max))
		if (!next) {
			text = `Rentals over ${row.scope_duration.max} days are not protected by “${row.name || 'this protection'}” — add a band for them or clear the upper bound.`
		}
	}
	if (!text) return null
	return (
		<Stack direction="row" gap={0.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
			<WarningIcon sx={{ fontSize: 14, color: dsColorNew.noteAmber, mt: '2px' }}/>
			<Typography sx={{ ...warningBodySx, fontSize: 11.5 }}>{text}</Typography>
		</Stack>
	)
}

function MatrixPicker({ value, matrices, loading, onChange }) {
	const chosen = matrices.find(m => m.id === value) || null
	return (
		<Stack direction="row" gap={0.75} alignItems="center">
			<Select size="small" displayEmpty value={value || ''}
			        onChange={(e) => onChange(e.target.value)}
			        sx={{ minWidth: 190, ...inputSx }}>
				<MenuItem value="" disabled>
					{loading ? 'Loading matrices…' : 'Pick a matrix…'}
				</MenuItem>
				{matrices.map(m => (
					<MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
				))}
			</Select>
			{chosen && (
				<IconButton size="small" component="a"
				            href="?tab=price-matrix"
				            aria-label={`Open the matrix ${chosen.name}`}
				            title={`Open "${chosen.name}"`}
				            sx={{ color: dsColorNew.textMuted }}>
					<OpenInNewIcon sx={{ fontSize: 16 }}/>
				</IconButton>
			)}
			{!loading && matrices.length === 0 && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>
					none defined yet
				</Typography>
			)}
		</Stack>
	)
}

function HeadCell({ children, ...rest }) {
	return (
		<TableCell {...rest}
		           sx={{ ...headCellSx, py: 1.25 }}>
			{children}
		</TableCell>
	)
}
